import { type Component, createResource, createSignal, Show } from 'solid-js'
import { useUser } from '../../shared/providers/user-provider'
import { useCorisDashboard } from '../../shared/providers/coris/coris-dashboard.provider'
import { usePeriods } from '../../shared/providers/period.provider'
import { sticky } from '../../shared/directives/sticky'
import PeriodSelector from '../shared/components/PeriodSelector'
import type { CompareMode, SimplePeriodType } from '../../shared/helpers/date.functions'
import DashboardMetricCard from './DashboardMetricCard'
import DashboardLineChart from './DashboardLineChart'
import DashboardMediaList from './DashboardMediaList'
import DashboardDonutChart from './DashboardDonutChart'
import DashboardUsersSummary from './DashboardUserSummary'
import {
  SkeletonCard,
  SkeletonMetricCard,
  SkeletonChart,
  SkeletonDonutChart,
  SkeletonList,
  Skeleton
} from '../shared/components/Skeleton'
import { Icon } from '@iconify-icon/solid'
import { Trans } from '@mbarzda/solid-i18next'

// Prevent tree-shaking of directive
sticky

import { getDateRangesForCustomPeriod, getDateRangesForSimplePeriod } from '../../shared/helpers/date.functions'
import { hasBothPlanTypes } from '../../shared/helpers/plan-type.functions'

const Dashboard: Component = () => {
  const { organizationSlug } = useUser()
  const { getDashboard } = useCorisDashboard()
  const { engagementPeriod, setEngagementPeriod } = usePeriods()
  const [isStuck, setIsStuck] = createSignal(false)

  const [dashboardData, { mutate: mutateDashboard }] = createResource(
    () => {
      const organization = organizationSlug()
      const period = engagementPeriod()
      if (!organization) return null

      let dateParams: {
        fromDate: string
        toDate: string
        fromPrevious: string
        toPrevious: string
      }

      if (period.selectedPeriod === 'custom' && period.customDateRange) {
        dateParams = getDateRangesForCustomPeriod(period.customDateRange.from, period.customDateRange.to)
      } else {
        dateParams = getDateRangesForSimplePeriod(period.selectedPeriod)
      }

      return {
        organization,
        ...dateParams
      }
    },
    async (params) => {
      if (!params) return null
      return await getDashboard.execute(params)
    }
  )

  const handlePeriodChange = (period: SimplePeriodType) => {
    mutateDashboard(undefined)
    setEngagementPeriod({ selectedPeriod: period })
  }

  const handleCustomRangeChange = (from: Date, to: Date) => {
    mutateDashboard(undefined)
    setEngagementPeriod({ customDateRange: { from, to } })
  }

  const handleCompareModeChange = (mode: CompareMode) => {
    mutateDashboard(undefined)
    setEngagementPeriod({ compareMode: mode })
  }

  const handleCompareCustomRangeChange = (from: Date, to: Date) => {
    mutateDashboard(undefined)
    setEngagementPeriod({ compareCustomRange: { from, to } })
  }

  // The artist offers both subscription types when they currently have paid subscribers in both a
  // monthly and a yearly plan. paidPlans is a current snapshot from the subscribers table, so it
  // tells us the offered types independently of how many new subscribers landed in the period.
  const offersBothPlanTypes = () => hasBothPlanTypes(dashboardData()?.overview.paidPlans ?? [])

  return (
    <div class="flex flex-col gap-4 w-full p-6">
      <div class="mx-auto w-full max-w-[1280px] flex flex-col gap-8">
        <Show
          when={dashboardData()?.overview}
          fallback={
            <div class="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 [&>div]:min-w-[120px] [&>div]:snap-start [&>div]:shrink-0 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:pb-0 md:snap-none">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          }
        >
          <DashboardUsersSummary
            totalUsers={dashboardData()!.overview.totalUsers}
            paidUsers={dashboardData()!.overview.paidUsers}
            freeUsers={dashboardData()!.overview.freeUsers}
            freeSubscribers={dashboardData()!.overview.freeSubscribers}
            churnedUsers={dashboardData()!.overview.churnedUsers}
            incompleteSignups={dashboardData()!.overview.incompleteSignups}
            totalMembers={dashboardData()!.overview.totalMembers}
            activeMembers={dashboardData()!.overview.activeMembers}
            inactiveMembers={dashboardData()!.overview.inactiveMembers}
            paidPlans={dashboardData()!.overview.paidPlans}
          />
        </Show>
      </div>
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 py-6 -mx-6 px-6 transition-colors duration-200"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent border-b border-transparent': !isStuck()
        }}
      >
        <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <PeriodSelector
            selectedPeriod={engagementPeriod().selectedPeriod}
            onPeriodChange={handlePeriodChange}
            customDateRange={engagementPeriod().customDateRange}
            onCustomRangeChange={handleCustomRangeChange}
            compareMode={engagementPeriod().compareMode}
            onCompareModeChange={handleCompareModeChange}
            compareCustomRange={engagementPeriod().compareCustomRange}
            onCompareCustomRangeChange={handleCompareCustomRangeChange}
          />
        </div>
      </div>
      <div class="mx-auto w-full max-w-[1280px] flex flex-col gap-8">
        {/* User Metrics Section */}
        <div class="flex flex-col gap-4">
          <h2 class="text-lg font-bold text-gray-900">
            <Trans key="ls_dashboard_user_metrics" />
          </h2>
          <Show
            when={dashboardData()?.userMetrics}
            fallback={
              <>
                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                  <SkeletonMetricCard />
                  <SkeletonMetricCard />
                  <SkeletonMetricCard />
                  <SkeletonMetricCard />
                </div>
                <SkeletonChart legendItems={3} />
              </>
            }
          >
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              <DashboardMetricCard
                titleKey="ls_dashboard_unique_visitors"
                value={dashboardData()!.userMetrics.uniqueVisitors.current}
                diff={dashboardData()!.userMetrics.uniqueVisitors.diff}
                icon={<Icon icon="heroicons:user-group" class="text-[1.5rem]" />}
              />
              <DashboardMetricCard
                titleKey="ls_dashboard_new_subscribed_users"
                value={dashboardData()!.userMetrics.newSubscribedUsers.current}
                diff={dashboardData()!.userMetrics.newSubscribedUsers.diff}
                icon={<Icon icon="heroicons:currency-dollar" class="text-[1.5rem]" />}
                breakdown={
                  offersBothPlanTypes()
                    ? dashboardData()!
                        .userMetrics.newSubscribersByPlan.filter((p) => p.count > 0)
                        .map((p) => ({
                          label: p.plan,
                          value: p.count
                        }))
                    : undefined
                }
              />
              <DashboardMetricCard
                titleKey="ls_dashboard_new_free_subscribers"
                value={dashboardData()!.userMetrics.newFreeSubscribers.current}
                diff={dashboardData()!.userMetrics.newFreeSubscribers.diff}
                icon={<Icon icon="heroicons:user-plus" class="text-[1.5rem]" />}
              />
              <DashboardMetricCard
                titleKey="ls_dashboard_churned_users"
                value={dashboardData()!.userMetrics.churnedUsers.current}
                diff={dashboardData()!.userMetrics.churnedUsers.diff}
                icon={<Icon icon="heroicons:user-minus" class="text-[1.5rem]" />}
              />
            </div>
            <DashboardLineChart periods={dashboardData()!.userMetrics.periods} />
          </Show>
        </div>

        {/* Content Sections Grid */}
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Songs Section */}
          <div class="flex flex-col gap-4 h-full">
            <h3 class="text-lg font-bold text-gray-900">
              <Trans key="ls_sidebar_songs" />
            </h3>
            <Show
              when={dashboardData()?.mediaTotals}
              fallback={
                <Skeleton class="flex flex-col gap-6 p-6 bg-white rounded-lg border border-gray-200 shadow-sm flex-1">
                  <SkeletonMetricCard variant="none" />
                  <hr class="border-gray-100" />
                  <SkeletonList items={5} />
                </Skeleton>
              }
            >
              <div class="flex flex-col gap-6 p-6 bg-white rounded-lg border border-gray-200 shadow-sm flex-1">
                <DashboardMetricCard
                  variant="none"
                  titleKey="ls_dashboard_songs_played"
                  value={dashboardData()!.mediaTotals.songsPlayed.current}
                  diff={dashboardData()!.mediaTotals.songsPlayed.diff}
                  icon={<Icon icon="heroicons:musical-note" class="text-[1.5rem]" />}
                />
                <hr class="border-gray-100" />
                <DashboardMediaList
                  titleKey="ls_dashboard_most_played_songs"
                  items={dashboardData()!.mediaTotals.mostPlayedSongs}
                  mediaType="song"
                />
              </div>
            </Show>
          </div>

          {/* Videos Section */}
          <div class="flex flex-col gap-4 h-full">
            <h3 class="text-lg font-bold text-gray-900">
              <Trans key="ls_sidebar_videos" />
            </h3>
            <Show
              when={dashboardData()?.mediaTotals}
              fallback={
                <Skeleton class="flex flex-col gap-6 p-6 bg-white rounded-lg border border-gray-200 shadow-sm flex-1">
                  <SkeletonMetricCard variant="none" />
                  <hr class="border-gray-100" />
                  <SkeletonList items={5} />
                </Skeleton>
              }
            >
              <div class="flex flex-col gap-6 p-6 bg-white rounded-lg border border-gray-200 shadow-sm flex-1">
                <DashboardMetricCard
                  variant="none"
                  titleKey="ls_dashboard_videos_viewed"
                  value={dashboardData()!.mediaTotals.videosViewed.current}
                  diff={dashboardData()!.mediaTotals.videosViewed.diff}
                  icon={<Icon icon="heroicons:video-camera" class="text-[1.5rem]" />}
                />
                <hr class="border-gray-100" />
                <DashboardMediaList
                  titleKey="ls_dashboard_most_viewed_videos"
                  items={dashboardData()!.mediaTotals.mostViewedVideos}
                  mediaType="video"
                />
              </div>
            </Show>
          </div>

          {/* Events Section */}
          <div class="flex flex-col gap-4">
            <h3 class="text-lg font-bold text-gray-900">
              <Trans key="ls_dashboard_events" />
            </h3>
            <Show when={dashboardData()?.events} fallback={<SkeletonDonutChart legendItems={4} />}>
              <DashboardDonutChart
                titleKey=""
                items={[
                  {
                    name: 'Buy Now Clicks',
                    value: dashboardData()!.events.buyNowClicks.current,
                    diff: dashboardData()!.events.buyNowClicks.diff,
                    color: '#3B82F6'
                  },
                  {
                    name: 'Presale Codes Redeemed',
                    value: dashboardData()!.events.presaleCodesRedeemed.current,
                    diff: dashboardData()!.events.presaleCodesRedeemed.diff,
                    color: '#10B981'
                  },
                  {
                    name: 'Meet & Greet Redeemed',
                    value: dashboardData()!.events.meetAndGreetRedeemed.current,
                    diff: dashboardData()!.events.meetAndGreetRedeemed.diff,
                    color: '#A855F7'
                  },
                  {
                    name: 'Early Access Redeemed',
                    value: dashboardData()!.events.rsvpRedeemed.current,
                    diff: dashboardData()!.events.rsvpRedeemed.diff,
                    color: '#EF4444'
                  }
                ]}
              />
            </Show>
          </div>

          {/* Community Section */}
          <div class="flex flex-col gap-4">
            <h3 class="text-lg font-bold text-gray-900">
              <Trans key="ls_dashboard_community" />
            </h3>
            <Show when={dashboardData()?.community} fallback={<SkeletonDonutChart legendItems={4} />}>
              <DashboardDonutChart
                titleKey=""
                items={[
                  {
                    name: 'Viewed Posts',
                    value: dashboardData()!.community.viewedPosts.current,
                    diff: dashboardData()!.community.viewedPosts.diff,
                    color: '#3B82F6'
                  },
                  {
                    name: 'Posts Created',
                    value: dashboardData()!.community.postCreated.current,
                    diff: dashboardData()!.community.postCreated.diff,
                    color: '#10B981'
                  },
                  {
                    name: 'Comments',
                    value: dashboardData()!.community.comments.current,
                    diff: dashboardData()!.community.comments.diff,
                    color: '#A855F7'
                  },
                  {
                    name: 'Upvotes',
                    value: dashboardData()!.community.upvotes.current,
                    diff: dashboardData()!.community.upvotes.diff,
                    color: '#EF4444'
                  }
                ]}
              />
            </Show>
          </div>
        </div>

        {/* Chat Section */}
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-bold text-gray-900">
            <Trans key="ls_dashboard_chat" />
          </h3>
          <Show
            when={dashboardData()?.chat}
            fallback={
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SkeletonMetricCard />
                <SkeletonMetricCard />
              </div>
            }
          >
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DashboardMetricCard
                titleKey="ls_dashboard_messages_sent"
                value={dashboardData()!.chat.messagesSent.current}
                diff={dashboardData()!.chat.messagesSent.diff}
                icon={<Icon icon="heroicons:chat-bubble-left-right" class="text-[1.5rem]" />}
              />
              <DashboardMetricCard
                titleKey="ls_dashboard_unique_messages"
                value={dashboardData()!.chat.uniqueMessages.current}
                diff={dashboardData()!.chat.uniqueMessages.diff}
                icon={<Icon icon="heroicons:chat-bubble-oval-left" class="text-[1.5rem]" />}
              />
            </div>
          </Show>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
